import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Item { label: string; to: string }

/**
 * ⌘K / Ctrl-K command palette for fast navigation across the (now large) app.
 * Filters the same nav items the user can already see, so it never offers a
 * page they can't reach. Keyboard-first: arrows to move, Enter to go, Esc to close.
 */
export default function CommandPalette({ items, open, onClose }: { readonly items: Item[]; readonly open: boolean; readonly onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (open) { setQ(''); setSel(0); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);
  useEffect(() => { setSel(0); }, [q]);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? items.filter((i) => i.label.toLowerCase().includes(t)) : items;
  }, [q, items]);

  if (!open) return null;

  const go = (to: string) => { onClose(); navigate(to); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(results.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = results[sel]; if (r) go(r.to); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/30 px-4 pt-[12vh]" onClick={onClose} role="dialog" aria-modal="true" aria-label="Command palette">
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Jump to a page…"
          aria-label="Search pages"
          className="w-full border-b border-gray-100 px-4 py-3 text-sm outline-none"
        />
        <ul className="max-h-80 overflow-y-auto py-1">
          {results.length === 0 ? (
            <li className="px-4 py-3 text-sm text-gray-400">No matches.</li>
          ) : results.map((r, i) => (
            <li key={r.to}>
              <button
                type="button"
                onClick={() => go(r.to)}
                onMouseEnter={() => setSel(i)}
                className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm ${i === sel ? 'bg-brand-50 text-brand-800' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <span>{r.label}</span>
                <span className="shrink-0 text-xs text-gray-300">{r.to}</span>
              </button>
            </li>
          ))}
        </ul>
        <p className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400">↑↓ to move · Enter to open · Esc to close</p>
      </div>
    </div>
  );
}
